import React, { useState, useEffect, useRef, useCallback } from 'react';
import { oracleDeck, uiText } from '../../data/oracle/oracleDeckData';
import { useGlow } from '../../hooks/useGlow';
import { LockClosedIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getOracleReading } from '../../services/geminiService';
import AIReadingDisplay from './AIReadingDisplay';
import OracleCreditManager from './OracleCreditManager';
import CooldownTimer from '../common/CooldownTimer';

const UNLOCK_CODE = "PORTALNUEVO"; 
const STORAGE_KEY = "fiveCardOracleDaily"; 

// --- SUBCOMPONENTE: Icono / Miniatura en Cruz ---
const MiniCrossSpread = ({ cards = [], isStatic = false, onSlotClick = null, className = "" }) => {
  const isSlotRevealed = (idx) => {
    if (isStatic || cards.length === 0) return true;
    return Boolean(cards[idx]?.isRevealed);
  };

  const renderSlot = (idx, label, isCenter = false) => {
    const revealed = isSlotRevealed(idx);
    return (
      <button
        type="button"
        onClick={() => onSlotClick && onSlotClick(idx)}
        disabled={!onSlotClick}
        aria-label={label}
        className={`w-2.5 h-3.5 sm:w-3 sm:h-4 rounded-[2px] border transition-all duration-300 ${
          onSlotClick ? 'cursor-pointer active:scale-125 hover:scale-110' : 'cursor-default'
        } ${
          isCenter
            ? revealed 
              ? 'bg-amber-400 border-amber-200 shadow-[0_0_8px_rgba(251,191,36,0.9)] scale-110' 
              : 'bg-purple-950/40 border-purple-500/30 opacity-50'
            : revealed
              ? 'bg-purple-400 border-purple-200 shadow-[0_0_6px_rgba(192,132,252,0.8)]'
              : 'bg-purple-950/40 border-purple-500/20 opacity-40'
        }`} 
      />
    );
  };

  return (
    <div 
      className={`inline-grid grid-cols-3 grid-rows-3 gap-1 p-1.5 bg-gray-950/80 border border-purple-500/25 rounded-lg shadow-inner backdrop-blur-sm ${className}`}
      title="Tirada en Cruz (5 Cartas)"
    >
      {/* Fila 1: Arriba (Corona - Pos 5) */}
      <div />
      {renderSlot(4, "Corona")}
      <div />

      {/* Fila 2: Izq (Pasado - Pos 2) | Centro (Corazón - Pos 1) | Der (Futuro - Pos 3) */}
      {renderSlot(1, "Pasado")}
      {renderSlot(0, "Corazón", true)}
      {renderSlot(2, "Futuro")}

      {/* Fila 3: Abajo (Raíz - Pos 4) */}
      <div />
      {renderSlot(3, "Raíz")}
      <div />
    </div>
  );
};

// --- SUBCOMPONENTE: Carta Individual ---
const CardSlot = ({ card, isReversed, isRevealed, onClick, positionLabel, extraClasses = "" }) => {
  return (
    <div className={`flex flex-col items-center gap-2 ${extraClasses}`}>
      <span className="text-purple-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase opacity-80 h-6 text-center whitespace-nowrap">
        {positionLabel}
      </span>

      <div 
        onClick={!isRevealed ? onClick : undefined}
        className={`relative w-32 h-48 sm:w-40 sm:h-60 md:w-44 md:h-64 cursor-pointer perspective-1000 group ${!isRevealed ? 'hover:-translate-y-2' : ''} transition-transform duration-300`}
      >
        <div className={`relative w-full h-full duration-700 preserve-3d transition-transform ${isRevealed ? 'rotate-y-180' : ''}`}>
          <div className="absolute inset-0 backface-hidden w-full h-full">
            <img
              src={oracleDeck[0].cardBack}
              alt="Reverso"
              className="w-full h-full object-cover rounded-xl border-2 border-purple-400/30 shadow-lg shadow-purple-900/40"
            />
          </div>
          <div className="absolute inset-0 backface-hidden w-full h-full rotate-y-180 bg-gray-900 rounded-xl overflow-hidden border-2 border-sky-400/50 shadow-sky-500/20 shadow-2xl">
            <img
              src={card.image}
              alt={card.data?.name}
              className={`w-full h-full object-cover transition-transform duration-700 ${isReversed ? 'rotate-180' : ''}`}
            />
            {isReversed && <div className="absolute inset-0 bg-indigo-900/10 pointer-events-none mix-blend-overlay"></div>}
          </div>
        </div>
      </div>

      {/* Información Detallada Debajo de la Carta */}
      <div className={`text-center transition-all duration-700 flex flex-col items-center gap-0.5 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
        <span className="text-sky-400 text-[9px] font-bold tracking-widest uppercase bg-sky-950/30 px-1.5 py-0.5 rounded border border-sky-500/20">
            {card.data?.category}
        </span>

        <h3 className="text-gray-100 font-bold text-xs sm:text-sm md:text-base leading-tight uppercase tracking-tight">
            {card.data?.name}
        </h3>

        <p className="text-gray-400 text-[9px] sm:text-[10px] italic font-medium max-w-[120px] sm:max-w-[150px] leading-tight">
            {card.data?.name2} <span className="text-gray-600 mx-0.5">•</span> {card.data?.name3}
        </p>

        {isReversed && (
            <span className="text-red-400 text-[9px] uppercase font-black tracking-tighter border border-red-400/30 px-2 py-0.5 rounded-full mt-1 bg-red-900/20">
                Invertida
            </span>
        )}
      </div>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL ---
const FiveCardStarSpread = ({ lang = 'es' }) => {
  const [cards, setCards] = useState([]); 
  const [topic, setTopic] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  
  const [aiReading, setAiReading] = useState('');
  const [isLoadingAi, setIsLoadingAi] = useState(false);
  const [storedDate, setStoredDate] = useState(null);

  const [isMinimapDismissed, setIsMinimapDismissed] = useState(false);

  // Referencias para observer y control de disparo único de IA
  const cardSlotRefs = useRef([]);
  const hasTriggeredAiRef = useRef(false);

  // Cantidad de cartas reveladas calculadas reactivamente
  const revealedCount = cards.filter(c => c.isRevealed).length;
  const allRevealed = cards.length === 5 && revealedCount === 5;

  const localUi = {
    spreadTitle: lang === 'en' ? 'The Star Spread (5 Cards)' : 'Tirada de la Estrella',
    spreadIntro: lang === 'en' ? 'Explore the roots, the heart, and the spiritual guidance of your situation.' : 'Explora la raíz, el corazón y la guía superior de tu situación actual.',
    inputPlaceholder: lang === 'en' ? 'What do you need clarity on?' : '¿En qué necesitas claridad hoy?',
    pos1: lang === 'en' ? '1. The Heart (Present)' : '1. El Corazón (Presente)',
    pos2: lang === 'en' ? '2. The Past (Influence)' : '2. El Pasado (Influencia)',
    pos3: lang === 'en' ? '3. The Future (Direction)' : '3. El Futuro (Dirección)',
    pos4: lang === 'en' ? '4. The Root (Unconscious)' : '4. La Raíz (Oculto)',
    pos5: lang === 'en' ? '5. The Crown (Advice)' : '5. La Corona (Consejo)',
    
    contactBtn: lang === 'en' ? 'Get Full Human Reading' : 'Solicitar Lectura Humana',
    aiBtn: lang === 'en' ? 'Ask the Oracle (AI)' : 'Pedir Interpretación al Oráculo (IA)',
    dailyLimitMsg: lang === 'en' ? 'You have already done your reading for today.' : 'Ya has realizado tu lectura de hoy.',
    closeMapLabel: lang === 'en' ? 'Close map' : 'Cerrar mapa'
  };

  const titleStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #9b977aff, #9e823cff, #776f5cff)' });
  const topicStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #d82995ff, #bf40c4ff, #af52d4ff)' });

  const scrollToCard = (index) => {
    const el = document.getElementById(`star-card-slot-${index}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleRevealCard = useCallback((index) => {
    setCards((prev) => {
      if (!prev[index] || prev[index].isRevealed) return prev;
      const next = [...prev];
      next[index] = { ...next[index], isRevealed: true };
      return next;
    });
  }, []);

  // --- OBSERVER PARA MOBILE: Voltea cartas automáticamente al scrollear ---
  useEffect(() => {
    if (cards.length === 0 || allRevealed) return;

    const isMobile = window.innerWidth < 768;
    if (!isMobile) return; // En desktop se interactúa por clic

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-card-index'));
            if (!isNaN(index)) {
              handleRevealCard(index);
              observer.unobserve(entry.target);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -15% 0px', // Se activa cuando la carta está bien dentro de la pantalla
        threshold: 0.35,
      }
    );

    cardSlotRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [cards, allRevealed, handleRevealCard]);

  // --- CARGA DESDE LOCALSTORAGE ---
  useEffect(() => {
    const checkLocalStorage = () => {
      try {
        const storedData = localStorage.getItem(STORAGE_KEY);

        if (storedData) {
          const {
            date,
            cards: savedCards,
            topic: savedTopic,
            reading: savedReading
          } = JSON.parse(storedData);

          const lastReadingDate = new Date(date);
          const today = new Date();

          setStoredDate(lastReadingDate);

          const diffInDays = Math.floor(
            (today - lastReadingDate) / (1000 * 60 * 60 * 24)
          );

          if (diffInDays < 3) {
            const restoredCards = savedCards.map(saved => {
              const fullCard = oracleDeck.find(c => c.id === saved.id);

              return {
                data: fullCard[lang],
                image: fullCard.image,
                originalId: fullCard.id,
                isReversed: saved.isReversed,
                isRevealed: true
              };
            });

            setCards(restoredCards);
            setTopic(savedTopic || '');

            if (savedReading) {
              setAiReading(savedReading.text);
              hasTriggeredAiRef.current = true;
            }
          } else {
            localStorage.removeItem(STORAGE_KEY);
            setCards([]);
            setTopic('');
            setAiReading('');
            hasTriggeredAiRef.current = false;
          }
        }
      } catch (error) {
        console.error("Error reading localStorage:", error);
      }
    };

    checkLocalStorage();
  }, [lang]);

  const handleStartSpread = () => {
    if (isDrawing || !topic.trim()) return;
    
    setIsDrawing(true);
    setAiReading('');
    setIsMinimapDismissed(false);
    hasTriggeredAiRef.current = false;

    setTimeout(() => {
      const deckCopy = [...oracleDeck];
      const selected = [];
      const today = new Date().toISOString().split('T')[0];
      
      for (let i = 0; i < 5; i++) {
        const randomIndex = Math.floor(Math.random() * deckCopy.length);
        const cardData = deckCopy[randomIndex];
        const inversionRules = [true, true, cardData.allowInvertedAdvice, true, cardData.allowInvertedAdvice]; 
        const canBeReversed = inversionRules[i];
        const isReversed = canBeReversed ? Math.random() < 0.5 : false;
        
        selected.push({
          data: cardData[lang],
          image: cardData.image,
          originalId: cardData.id,
          isReversed: isReversed,
          isRevealed: false
        });
        
        deckCopy.splice(randomIndex, 1);
      }

      setCards(selected);
      setIsDrawing(false);

      const dataToSave = {
        date: today,
        topic: topic,
        cards: selected.map(c => ({ id: c.originalId, isReversed: c.isReversed })),
        reading: ''
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));

    }, 1500);
  };

  const handleGetAIReading = useCallback(async () => {
    if (cards.length < 5 || isLoadingAi) return;
    setIsLoadingAi(true);
    
    const cardsForAi = cards.map((c, index) => {
        const positions = [localUi.pos1, localUi.pos2, localUi.pos3, localUi.pos4, localUi.pos5];
        return {
            name: `${c.data.name} (${c.data.name2}, ${c.data.name3}) - Categoría: ${c.data.category}`,
            positionName: positions[index],
            isReversed: c.isReversed,
            breaf: c.data.breaf || c.data.description,
            keywords: c.data.keywords
        };
    });

    const reading = await getOracleReading(topic, localUi.spreadTitle, cardsForAi, lang);
    
    if (reading.success) {
      setAiReading(reading.text);
      const now = new Date().toISOString();
      setStoredDate(now);
    }
    
    setIsLoadingAi(false);

    try {
        const storedData = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (storedData.date) {
            const updatedData = { ...storedData, reading: reading };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedData));
        }
    } catch (e) {
        console.error("Error guardando lectura en LS", e);
    }
  }, [cards, isLoadingAi, localUi.pos1, localUi.pos2, localUi.pos3, localUi.pos4, localUi.pos5, localUi.spreadTitle, topic, lang]);

  // --- DISPARADOR AUTOMÁTICO DE LA IA CUANDO LAS 5 ESTÁN REVELADAS ---
  useEffect(() => {
    if (allRevealed && !aiReading && !isLoadingAi && !hasTriggeredAiRef.current) {
      hasTriggeredAiRef.current = true;
      // Breve pausa para contemplar el volteo de la última carta antes de cargar la IA
      const timer = setTimeout(() => {
        handleGetAIReading();
      }, 700);

      return () => clearTimeout(timer);
    }
  }, [allRevealed, aiReading, isLoadingAi, handleGetAIReading]);

  const generateContactLink = () => {
    const cardNames = cards.map((c, i) => `${i+1}. ${c.data.name} (${c.isReversed ? 'Inv' : 'Der'})`).join('\n');
    const message = `Hola, hice la Tirada de la Estrella sobre: "${topic}".\nCartas:\n${cardNames}\n¿Me das la interpretación personalizada?`;
    return `https://wa.me/5491150254270?text=${encodeURIComponent(message)}`; 
  };

  // --- VISTA INICIAL ---
  if (cards.length === 0) {
    const isInputValid = topic.trim().length > 0;
    return (
      <div className="flex flex-col items-center justify-center text-center p-8 min-h-[70vh] animate-fade-in w-full max-w-4xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-3">
          <MiniCrossSpread isStatic />
          <h1 style={titleStyle} className="text-3xl md:text-5xl font-bold">{localUi.spreadTitle}</h1>
        </div>
        <p className="text-lg text-gray-400 mb-8 max-w-md mx-auto">{localUi.spreadIntro}</p>
        
        <div className="relative w-full max-w-sm mb-8 group z-20">
            <div className={`absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-sky-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 ${isInputValid ? 'opacity-100' : 'opacity-50'}`}></div>
            <input 
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={localUi.inputPlaceholder}
                className="relative w-full bg-gray-900 text-white rounded-lg p-4 text-center border border-gray-700 focus:outline-none focus:bg-gray-800 placeholder-gray-500"
            />
        </div>

        <div className="relative">
            <button
            onClick={handleStartSpread}
            disabled={isDrawing || !isInputValid}
            className={`relative group w-48 h-64 perspective-1000 transition-all duration-500 ${!isInputValid ? 'cursor-not-allowed opacity-50 grayscale' : 'cursor-pointer'}`}
            >
            {[...Array(5)].map((_, i) => (
                <img
                key={i}
                src={oracleDeck[0].cardBack}
                alt="Mazo"
                className={`absolute inset-0 w-full h-full object-cover rounded-xl border-2 border-purple-400/30 shadow-lg
                            transition-all duration-500 ease-in-out
                            ${isDrawing ? 'animate-pulse' : isInputValid ? 'group-hover:-translate-y-4 shadow-purple-500/50' : ''}`}
                style={{
                    transform: `rotateZ(${i * 2 - 4}deg) translateY(${i * -1}px)`,
                    zIndex: 5 - i,
                }}
                />
            ))}
            {!isInputValid && (
                <div className="absolute inset-0 flex items-center justify-center z-20">
                    <LockClosedIcon className="w-16 h-16 text-gray-400 opacity-80" />
                </div>
            )}
            </button>
        </div>

        <p className={`mt-10 font-semibold transition-colors duration-300 ${isInputValid ? 'text-sky-300 animate-pulse' : 'text-gray-600'}`}>
          {isDrawing ? "Canalizando energía de 5 puntos..." : isInputValid ? "Toca para revelar la Estrella" : "Escribe tu pregunta"}
        </p>
      </div>
    );
  }

  // --- VISTA DE LA TIRADA CON CARTAS ---
  return (
    <div className="w-full max-w-7xl mx-auto p-2 md:p-8 min-h-[90vh] flex flex-col items-center relative">
      
      {/* 🧭 MINI-MAPA FLOTANTE FIJO EN MOBILE */}
      {!aiReading && !isMinimapDismissed && (
        <aside 
          aria-label="Mapa en cruz flotante"
          className="fixed bottom-6 right-4 z-40 md:hidden flex flex-col items-center bg-gray-950/90 backdrop-blur-md p-2 pt-1.5 rounded-2xl border border-purple-500/40 shadow-2xl shadow-purple-950/90 animate-fade-in"
        >
          <div className="w-full flex items-center justify-between gap-2 mb-1 px-1">
            <span className="text-[8px] uppercase tracking-wider font-extrabold text-purple-300/80">
              Mapa
            </span>
            <button
              type="button"
              onClick={() => setIsMinimapDismissed(true)}
              aria-label={localUi.closeMapLabel}
              className="text-gray-400 hover:text-white p-0.5 rounded-full hover:bg-white/10 active:scale-95 transition-all"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>

          <MiniCrossSpread cards={cards} onSlotClick={scrollToCard} />
        </aside>
      )}

      {/* Cabecera general */}
      <header className="text-center mb-8 animate-fade-in-down w-full flex flex-col items-center">
        <div className="flex items-center justify-center gap-3 mb-2">
            <MiniCrossSpread cards={cards} onSlotClick={scrollToCard} />
            <span style={titleStyle} className="text-sm sm:text-base font-bold uppercase tracking-[0.2em] text-purple-400">
                {localUi.spreadTitle}
            </span>
        </div>

        <h2 style={topicStyle} className="text-2xl md:text-3xl font-bold text-white max-w-3xl mx-auto break-words">
            "{topic}"
        </h2>
        {allRevealed && (
             <p className="text-xs text-gray-500 mt-2">{localUi.dailyLimitMsg}</p>
        )}
      </header>

      {/* Grilla de la Estrella (5 puntos en cruz) */}
      <main className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-3 gap-8 md:gap-x-12 md:gap-y-8 mb-20 w-full place-items-center max-w-4xl mx-auto">
        
        {/* Pos 5: Corona (Arriba) */}
        <div 
          id="star-card-slot-4" 
          ref={(el) => (cardSlotRefs.current[4] = el)}
          data-card-index={4}
          className="order-1 md:order-none md:col-start-2 md:row-start-1 z-10 scroll-mt-24"
        >
             <CardSlot 
                card={cards[4]} isReversed={cards[4].isReversed} isRevealed={cards[4].isRevealed} 
                onClick={() => handleRevealCard(4)} positionLabel={localUi.pos5} 
            />
        </div>

        {/* Pos 2: Pasado (Izquierda) */}
        <div 
          id="star-card-slot-1" 
          ref={(el) => (cardSlotRefs.current[1] = el)}
          data-card-index={1}
          className="order-2 md:order-none md:col-start-1 md:row-start-2 scroll-mt-24"
        >
            <CardSlot 
                card={cards[1]} isReversed={cards[1].isReversed} isRevealed={cards[1].isRevealed} 
                onClick={() => handleRevealCard(1)} positionLabel={localUi.pos2} 
            />
        </div>

        {/* Pos 1: Centro (Corazón) */}
        <div 
          id="star-card-slot-0" 
          ref={(el) => (cardSlotRefs.current[0] = el)}
          data-card-index={0}
          className="order-3 md:order-none md:col-start-2 md:row-start-2 z-20 scroll-mt-24"
        >
            <CardSlot 
                card={cards[0]} isReversed={cards[0].isReversed} isRevealed={cards[0].isRevealed} 
                onClick={() => handleRevealCard(0)} positionLabel={localUi.pos1}
                extraClasses="scale-110" 
            />
        </div>

        {/* Pos 3: Futuro (Derecha) */}
        <div 
          id="star-card-slot-2" 
          ref={(el) => (cardSlotRefs.current[2] = el)}
          data-card-index={2}
          className="order-4 md:order-none md:col-start-3 md:row-start-2 scroll-mt-24"
        >
            <CardSlot 
                card={cards[2]} isReversed={cards[2].isReversed} isRevealed={cards[2].isRevealed} 
                onClick={() => handleRevealCard(2)} positionLabel={localUi.pos3} 
            />
        </div>

        {/* Pos 4: Raíz (Abajo) */}
        <div 
          id="star-card-slot-3" 
          ref={(el) => (cardSlotRefs.current[3] = el)}
          data-card-index={3}
          className="order-5 md:order-none md:col-start-2 md:row-start-3 scroll-mt-24"
        >
             <CardSlot 
                card={cards[3]} isReversed={cards[3].isReversed} isRevealed={cards[3].isRevealed} 
                onClick={() => handleRevealCard(3)} positionLabel={localUi.pos4} 
            />
        </div>
      </main>

      <section className={`w-full max-w-3xl flex flex-col items-center gap-8 transition-all duration-1000 ${allRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}>
        
        {/* Sección de Lectura Automática */}
        <div className="w-full">
            {isLoadingAi || aiReading ? (
                <AIReadingDisplay 
                    interpretation={aiReading} 
                    isLoading={isLoadingAi} 
                    topic={topic}
                    cards={cards.map((c, i) => {
                        const positions = [localUi.pos1, localUi.pos2, localUi.pos3, localUi.pos4, localUi.pos5];
                        return { ...c, positionName: positions[i] };
                    })}
                    spreadName={localUi.spreadTitle}
                />
            ) : (
                /* Fallback por si la IA necesitase ser reintentada manualmente */
                <div className="flex justify-center">
                    <button 
                        onClick={handleGetAIReading}
                        className="bg-purple-600 hover:bg-purple-500 text-white font-semibold py-3 px-8 rounded-full shadow-lg shadow-purple-900/50 transition-all flex items-center gap-2"
                    >
                        <SparklesIcon className="w-5 h-5" />
                        {localUi.aiBtn}
                    </button>
                </div>
            )}
        </div>
        
        <a 
            href={generateContactLink()}
            target="_blank" 
            rel="noopener noreferrer"
            className="py-4 px-10 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:from-teal-400 text-white rounded-full font-bold text-lg transition-all duration-300 shadow-lg shadow-emerald-900/50 hover:scale-105 hover:shadow-emerald-500/40 flex items-center gap-2 text-center"
        >
            {localUi.contactBtn}
        </a>

        <CooldownTimer
          startDate={storedDate}
          daysLimit={3}
          lang={lang}
        />

        <footer className="mb-8 pt-8 border-t border-gray-800 w-full flex flex-col items-center">
            <OracleCreditManager 
                lang={lang}
                onCreditUsed={() => {
                    localStorage.removeItem(STORAGE_KEY);
                    setCards([]);
                    setTopic('');
                    setAiReading('');
                    hasTriggeredAiRef.current = false;
                    setIsMinimapDismissed(false);
                }}
            />
        </footer>
      </section>
    </div>
  );
};

export default FiveCardStarSpread;